import styled, { keyframes } from "styled-components";

interface ColorProps {
  $color?: string;
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ----------------------------- layout ----------------------------- */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 6px 6px 120px;
  color: var(--text);
  animation: ${fadeUp} 0.4s ease both;
`;

/* ----------------------------- header ----------------------------- */
export const HeaderBar = styled.header`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 22px;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--gradient-brand);
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  span.eyebrow {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text);
`;

export const StatusChip = styled.span<ColorProps>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;

  border-radius: 9px;
  font-size: 1rem;
  font-weight: 700;
  color: ${(p) => p.$color || "var(--accent)"};
  background: ${(p) =>
    `color-mix(in srgb, ${p.$color || "var(--accent)"} 14%, transparent)`};
  border: 1px solid
    ${(p) =>
      `color-mix(in srgb, ${p.$color || "var(--accent)"} 38%, transparent)`};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.$color || "var(--accent)"};
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

export const StatusSelectWrap = styled.div`
  min-width: 210px;
`;

export const PdfActions = styled.div`
  display: flex;
  align-items: stretch;
  gap: 10px;
`;

export const PdfButton = styled.button<ColorProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 70px;
  padding: 8px 12px;
  border-radius: 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text-muted);
  cursor: pointer;
  transition:
    transform 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    color 0.16s ease;

  svg {
    color: ${(p) => p.$color || "var(--accent)"};
    font-size: 22px;
  }

  small {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    border-color: ${(p) => p.$color || "var(--border-strong)"};
    box-shadow: var(--shadow);
    color: var(--text);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    svg {
      color: var(--text-dim);
    }
  }
`;

/* ---------------------------- step banner -------------------------- */
export const StepBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--accent) 8%, var(--surface));
  border: 1px dashed color-mix(in srgb, var(--accent) 40%, transparent);
  color: var(--text-muted);
  font-size: 0.9rem;

  svg {
    color: var(--accent);
    flex-shrink: 0;
  }

  strong {
    color: var(--text);
    font-weight: 700;
  }
`;

/* ------------------------------ cards ------------------------------ */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border);
`;

export const CardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--text);

  svg {
    color: var(--accent);
    font-size: 20px;
  }
`;

export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const CardBody = styled.div`
  padding: 8px 18px 16px;
`;

export const DeviceTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 12%, transparent);
`;

/* --------------------------- info list ----------------------------- */
export const InfoList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 6px 0 0;
`;

export const InfoItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 4px;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }

  dt {
    font-size: 0.84rem;
    font-weight: 600;
    color: var(--text-muted);
  }

  dd {
    margin: 0;
    font-size: 0.92rem;
    font-weight: 600;
    color: var(--text);
    text-align: right;
  }
`;

/* ------------------------------ table ------------------------------ */
export const TableScroll = styled.div`
  max-height: 260px;
  overflow-y: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
  color: var(--text);

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--surface-2);
    color: var(--text-muted);
    font-weight: 700;
    font-size: 0.78rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    text-align: left;
    padding: 11px 12px;
    border-bottom: 1px solid var(--border);
  }

  tbody td {
    padding: 11px 12px;
    border-bottom: 1px solid var(--border);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

export const TableRow = styled.tr<{ $selected?: boolean }>`
  transition: background 0.15s ease;
  background: ${(p) =>
    p.$selected
      ? "color-mix(in srgb, var(--accent) 10%, transparent)"
      : "transparent"};

  &:hover {
    background: ${(p) =>
      p.$selected
        ? "color-mix(in srgb, var(--accent) 14%, transparent)"
        : "var(--surface-2)"};
  }

  td:first-child {
    border-left: 3px solid
      ${(p) => (p.$selected ? "var(--accent)" : "transparent")};
  }
`;

export const TableCell = styled.td``;

export const ActionsCell = styled.td`
  white-space: nowrap;
  text-align: right;
`;

export const Money = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: var(--text);
`;

/* --------------------------- empty state --------------------------- */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  text-align: center;
  color: var(--text-dim);
  font-size: 0.9rem;

  svg {
    font-size: 30px;
    color: var(--text-dim);
    opacity: 0.7;
  }
`;

/* --------------------------- summary bar --------------------------- */
export const SummaryBar = styled.div`
  position: fixed;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  width: min(1080px, calc(100% - 280px));
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 16px;
  background: var(--surface-glass);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border-strong);
  box-shadow: var(--shadow-lg);
  z-index: 20;

  @media (max-width: 980px) {
    width: calc(100% - 28px);
  }
`;

export const SummaryItem = styled.div<{ $accent?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 4px 14px;
  border-right: 1px solid var(--border);

  &:last-child {
    border-right: none;
  }

  span.label {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--text-muted);
  }

  span.value {
    font-family: var(--font-display);
    font-size: ${(p) => (p.$accent ? "1.5rem" : "1.2rem")};
    font-weight: 800;
    letter-spacing: -0.01em;
    color: ${(p) => (p.$accent ? "var(--accent)" : "var(--text)")};
    font-variant-numeric: tabular-nums;
  }
`;

/* --------------------------- centered msg -------------------------- */
export const CenterMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: var(--text-muted);
  font-size: 1rem;
`;
